import React from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, List, ListItem, ListItemIcon, ListItemText, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { getDealTriggers, getWorkflowSuggestions } from '../../utils/insightsHelper';

const AutomationItem = ({ title, description }) => (
  <div style={{ 
    padding: '8px 12px', 
    backgroundColor: '#f3f4f6', 
    borderRadius: '6px',
    fontSize: '13px'
  }}>
    <strong style={{ color: '#374151' }}>{title}:</strong>{' '}
    <span style={{ color: '#6b7280' }}>{description}</span>
  </div>
);

const TriggersModal = ({ open, onClose, objectType, data }) => {
  if (!data || objectType !== 'deals') return null;

  const triggers = getDealTriggers(data);
  const workflowSuggestion = getWorkflowSuggestions(data);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Active Triggers & Automation Opportunities
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {triggers.length > 0 ? (
          <>
            <h4 style={{ marginTop: 0, marginBottom: '16px', color: '#dc2626' }}>
              🚨 Active Triggers ({triggers.length})
            </h4>
            <List>
              {triggers.map((trigger, index) => (
                <ListItem 
                  key={index} 
                  sx={{ 
                    mb: 2, 
                    border: '1px solid #fee2e2', 
                    borderRadius: '8px',
                    backgroundColor: '#fef2f2'
                  }}
                >
                  <ListItemIcon>
                    <PlayCircleIcon sx={{ color: '#dc2626' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <strong>{trigger.trigger}</strong>
                        <Chip label="Action Required" size="small" color="error" />
                      </div>
                    }
                    secondary={
                      <div style={{ marginTop: '8px' }}>
                        <div style={{ marginBottom: '4px' }}>
                          <strong>Immediate Action:</strong> {trigger.action}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <AutorenewIcon sx={{ fontSize: '16px', color: '#7c3aed' }} />
                          <span style={{ color: '#7c3aed', fontSize: '14px' }}>
                            Automation: {trigger.automation}
                          </span>
                        </div>
                      </div>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        ) : (
          <p style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>
            No active triggers at this time. Deal is progressing normally.
          </p>
        )}

        <h4 style={{ marginTop: '24px', marginBottom: '16px', color: '#7c3aed' }}>
          💡 Automation Recommendations
        </h4>
        {workflowSuggestion && (
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#f3e8ff', 
            borderRadius: '8px',
            border: '1px solid #e9d5ff',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <h5 style={{ margin: 0, color: '#7c3aed', fontSize: '16px' }}>
                {workflowSuggestion.title}
              </h5>
              <Chip 
                label={workflowSuggestion.benefit} 
                size="small" 
                sx={{ backgroundColor: '#ddd6fe', color: '#6d28d9' }}
              />
            </div>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6, color: '#4c1d95' }}>
              {workflowSuggestion.description}
            </p>
          </div>
        )}
        
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#f9fafb', 
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
            <strong>Additional automation opportunities based on deal analysis:</strong>
          </p>
          <div style={{ display: 'grid', gap: '8px' }}>
            {triggers.length === 0 && (
              <>
                <AutomationItem 
                  title="Activity Monitoring" 
                  description="Set up alerts for deals without activity for 7+ days"
                />
                <AutomationItem 
                  title="Stage Velocity Tracking" 
                  description="Monitor time in each stage and alert on delays"
                />
                <AutomationItem 
                  title="Engagement Scoring" 
                  description="Automatically score deals based on activity patterns"
                />
              </>
            )}
            {triggers.some(t => t.trigger.includes('Email-Only')) && (
              <AutomationItem 
                title="Multi-Channel Outreach" 
                description="Rotate between email, calls, and SMS automatically"
              />
            )}
            {triggers.some(t => t.trigger.includes('Stuck')) && (
              <AutomationItem 
                title="Stage Progression Campaigns" 
                description="Automated nurture sequences for stalled deals"
              />
            )}
            {triggers.some(t => t.trigger.includes('Multiple Stakeholders')) && (
              <AutomationItem 
                title="Stakeholder Mapping" 
                description="Track and engage all decision makers systematically"
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TriggersModal;